import type { ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Mirrors the client form dialog so popovers/select portals inside the payment
// drawer do not accidentally dismiss the dialog when clicked.
const pointerDownWithPopperOpen = new WeakSet<Event>()
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    event => {
      if (document.querySelector('[data-radix-popper-content-wrapper]')) {
        pointerDownWithPopperOpen.add(event)
      }
    },
    true
  )
}

interface PaymentDrawerShellProps {
  open: boolean
  title: string
  onClose: () => void
  hideHeader?: boolean
  size?: 'short' | 'long'
  titleClassName?: string
  children: ReactNode
}

export function PaymentDrawerShell({
  open,
  title,
  onClose,
  hideHeader = false,
  titleClassName,
  children,
}: PaymentDrawerShellProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={next => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          className={cn(
            'tf-right-drawer-fixed-short border-[#e7e9ef] duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          )}
          aria-describedby={undefined}
          onInteractOutside={event => {
            const original = event.detail.originalEvent
            const eventTarget = original.target as Element | null
            if (
              pointerDownWithPopperOpen.has(original) ||
              eventTarget?.closest('[data-radix-popper-content-wrapper]')
            ) {
              event.preventDefault()
            }
          }}
        >
          {hideHeader ? (
            <DialogPrimitive.Title className='sr-only'>
              {title}
            </DialogPrimitive.Title>
          ) : (
            <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
              <DialogPrimitive.Title
                className={cn(
                  'min-w-0 flex-1 font-heading text-xl font-medium leading-[1.2] text-[#171a20]',
                  titleClassName
                )}
              >
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-9 shrink-0 rounded-xl bg-[#f4f5f8] text-[#5f6673] hover:bg-[#f4f5f8]'
                  aria-label='Close'
                  onClick={onClose}
                >
                  <X className='size-4' />
                </Button>
              </DialogPrimitive.Close>
            </header>
          )}

          <div className='min-h-0 flex-1 bg-[#f4f5f8]'>{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default PaymentDrawerShell
